import { isCarsVehiclesExtendedRadiusCategory } from '@kijiji/category'
import { ResultsView } from '@kijiji/generated/graphql-types'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { ClientRender } from '@/components/shared/client-render'
import { LowOrZeroResultsBanner } from '@/components/srp/low-or-zero-results/LowOrZeroResultsBanner'
import { SrpPagination } from '@/components/srp/pagination'
import { PopularRelatedKeywords } from '@/components/srp/popular-related-keywords/PopularRelatedKeywords'
import { ListingsDrawer } from '@/components/srp/search-list/ListingsDrawer'
import { RenderListingCards } from '@/components/srp/search-list/RenderListingCards'
import { RenderTopAds } from '@/components/srp/search-list/RenderTopAds'
import { PopularKeywordsWrapper } from '@/components/srp/search-list/styled'
import { SrpLoading } from '@/components/srp/srp-loading'
import { AdSenseSrpContainer } from '@/components-page/srp/advertisement/AdsenseSrpContainer'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { getSearchCategoryFromSearchQuery } from '@/domain/category/getSearchCategoryFromSearchQuery'
import { getUserLocationFromSearchQuery } from '@/domain/location/getUserLocationFromSearchQuery'
import {
  getExtendedRadius,
  hasExtendedRadiusListings,
  isLowInventorySearch,
  isZeroInventorySearch,
} from '@/domain/lowInventory'
import { useGetSrpState } from '@/features/search/hooks/useGetSrpState'
import { ImageSeoJsonLdWrapper } from '@/features/seo/components/ImageSeoJsonLdWrapper'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useListingsDrawerState } from '@/hooks/srp/useListingsDrawerState'
import { useLocale } from '@/hooks/useLocale'
import { RemoteParamKeys, useToggle } from '@/lib/firebase/hooks'
import { trackEvent } from '@/lib/ga'
import { getSearchPageType } from '@/lib/ga/utils/getSearchPageType'
import { type Listing } from '@/types/search'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { Spacing } from '@/ui/atoms/spacing'
import LowSrpImage from '~/public/next-assets/images/low-results-srp.png'

const ZeroResultsActionBox = dynamic(
  () => import('@/components/srp/zero-results/').then((mod) => mod.ZeroResultsActionBox),
  { ssr: true } // to prevent layout shift
)

const MiniLeaderboardAdSlot = dynamic(
  () =>
    import('@/components-page/srp/advertisement/MiniLeaderboardAdSlot').then(
      (mod) => mod.MiniLeaderboardAdSlot
    ),
  { ssr: false }
)

const PaginationLinkContainer = dynamic(
  () => import('./styled').then((mod) => mod.PaginationLinkContainer),
  { ssr: false }
)

export type SearchListProps = {
  extendedRadiusListings: Listing[]
  totalPageCount: number
  searchTitle?: string
}

export const SearchList = ({
  extendedRadiusListings = [],
  searchTitle,
  totalPageCount,
}: SearchListProps) => {
  const { t } = useTranslation([TRANSLATION_KEYS.SRP, TRANSLATION_KEYS.COMMON])
  const { apiLocale } = useLocale()
  const { colors, spacing } = useTheme()

  const { currentPage, viewType } = useGetSrpState()
  const { data, loadingResults } = useGetSearchResultsData()
  const { isListingsDrawerOpen } = useListingsDrawerState()

  const { pagination, searchQuery, results } = data || {}
  const { topListings: topAds = [], mainListings: listings = [] } = results || {}
  const { totalCount: totalListings = 0 } = pagination || {}

  const globalKeyword = searchQuery?.keywords ?? ''
  const location = getUserLocationFromSearchQuery(searchQuery)
  const category = getSearchCategoryFromSearchQuery(searchQuery?.category, apiLocale)

  /**
   * Toggles used in this component:
   * - EXTENDED_SEARCH_FOR_NULL_AND_LOW_SRP
   */
  const extendedSearchForLowOrZeroSrpToggle = useToggle(
    RemoteParamKeys.EXTENDED_SEARCH_FOR_NULL_AND_LOW_SRP
  )

  const extendedRadius = useMemo(
    () => getExtendedRadius(location?.area?.radius),
    [location?.area?.radius]
  )

  const isZeroSrp = useMemo(() => isZeroInventorySearch(totalListings), [totalListings])
  const isLowSrp = useMemo(() => isLowInventorySearch(totalListings), [totalListings])

  const searchPageType = useMemo(() => getSearchPageType(globalKeyword), [globalKeyword])

  const renderLowOrZeroSrpSuggestedContent = () => {
    const isVehiclesExtendedRadiusCategory = isCarsVehiclesExtendedRadiusCategory(category.id)
    if (isVehiclesExtendedRadiusCategory) {
      const userRadius = location?.area?.radius ?? 0
      return (
        <>
          <HeadlineText size="large" as="h3" color={colors.grey.primary}>
            {t('srp:filters.location.radial_location', { radius: userRadius + 100 })}
          </HeadlineText>
          <HeadlineText size="large" as="h3" color={colors.grey.primary}>
            {t('srp:filters.location.radial_location', { radius: userRadius + 250 })}
          </HeadlineText>
        </>
      )
    }

    return (
      <HeadlineText size="large" as="h3" color={colors.grey.primary}>
        {t('srp:zero_results.you_might_like')}
      </HeadlineText>
    )
  }

  useEffect(() => {
    if (isLowSrp) {
      trackEvent({ action: searchPageType, label: 'SuggestionBoxLoad' })
    }
  }, [isLowSrp, searchPageType])

  const listingsContent = (
    <Flex flexDirection="column" gap={spacing.default}>
      <>
        {loadingResults ? (
          <SrpLoading variant="listing-card" />
        ) : (
          <>
            {/* Provincial Top Ads (PTAs) need to display on ZSRPs */}
            {viewType !== ResultsView.Grid && (
              <RenderTopAds topAds={topAds} shouldRenderMapSRP={viewType === ResultsView.Map} />
            )}

            {viewType !== ResultsView.Map && (
              <PopularKeywordsWrapper>
                <PopularRelatedKeywords
                  categoryId={category.id.toString()}
                  keywords={searchQuery?.keywords}
                  location={{ id: location.id }}
                  locationArea={location.area}
                />
              </PopularKeywordsWrapper>
            )}

            {isZeroSrp && (
              <>
                <ZeroResultsActionBox />
                {extendedSearchForLowOrZeroSrpToggle?.enabled &&
                  renderLowOrZeroSrpSuggestedContent()}
              </>
            )}

            {!!listings.length && (
              <RenderListingCards listings={listings} topAds={topAds} currentView={viewType} />
            )}

            {isLowSrp && (
              <>
                {extendedSearchForLowOrZeroSrpToggle?.enabled && (
                  <>
                    <LowOrZeroResultsBanner
                      title={t('srp:low_results.all_categories.title')}
                      description={t('srp:low_results.all_categories.description')}
                      image={LowSrpImage}
                      imageHeight={85}
                      imageWidth={108}
                    />
                    {renderLowOrZeroSrpSuggestedContent()}
                  </>
                )}
                {/* The extended radius sections of the low/zero SRPs we get when the EXTENDED_SEARCH_FOR_NULL_AND_LOW_SRP toggle is enabled
                will differ from the existing one. As a result, we want to hide the existing section if the toggle is enabled, since that part
                of the feature has not been fully implemented yet */}
                {hasExtendedRadiusListings(extendedRadiusListings.length, extendedRadius) &&
                  !extendedSearchForLowOrZeroSrpToggle?.enabled && (
                    <>
                      <Spacing mTop={spacing.default} mBottom={spacing.defaultSmall}>
                        <HeadlineText
                          color={colors.grey.primary}
                          size="xLarge"
                          weight="medium"
                          as="h2"
                        >
                          {t('srp:extended_listings.title', { extendedRadius })}
                        </HeadlineText>
                      </Spacing>

                      <RenderListingCards
                        listings={extendedRadiusListings}
                        currentView={viewType}
                      />
                    </>
                  )}
                {!extendedSearchForLowOrZeroSrpToggle?.enabled && (
                  <Spacing mTop={spacing.giant}>
                    <ZeroResultsActionBox />
                  </Spacing>
                )}
              </>
            )}
          </>
        )}
      </>
      <AdSenseSrpContainer id="AFSBottom">
        {!!totalListings && <MiniLeaderboardAdSlot />}
      </AdSenseSrpContainer>
      <PaginationLinkContainer justifyContent="center" alignItems="center">
        {totalPageCount > 1 && (
          <SrpPagination selectedIndex={currentPage} totalPageCount={totalPageCount} />
        )}
      </PaginationLinkContainer>
    </Flex>
  )

  return (
    <>
      <ImageSeoJsonLdWrapper
        listings={listings.concat(topAds)}
        id="srp-listing-card-image-json-ld"
      />

      {isListingsDrawerOpen ? (
        <ClientRender>
          <ListingsDrawer viewType={viewType} searchTitle={searchTitle}>
            {listingsContent}
          </ListingsDrawer>
        </ClientRender>
      ) : (
        listingsContent
      )}
    </>
  )
}
